import React, { useState, useEffect } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { isStripeError } from '../../util/errors';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';

import {
  H4,
  Button,
  ExternalLink,
  InlineTextButton,
  FieldSelect,
  FieldRadioButton,
  Form,
  StripeBankAccountTokenInputField,
  FieldPhoneNumberInput,
  FieldTextInput,
} from '../../components';

import css from './StripeConnectAccountForm.module.css';

const CreateStripeAccountFields = props => {
  const routeConfiguration = useRouteConfiguration();
  const {
    disabled,
    countryLabel,
    showAsRequired,
    form,
    values,
    intl,
    currentUserId,
    marketplaceRootURL,
    supportedCountries,
    defaultMCC,
    currentUser,
  } = props;

  const protectedData = currentUser?.attributes?.profile?.protectedData;
  const {
    panCardName,
    panCardNumber,
    phoneNumber,
    GSTNumber,
    businessEntity,
    Quantity,
    BusinessName,
    AccountHolderName,
    IfscCode,
    BankAccountNumber,
  } = protectedData;
  const businessEntityOptions = [
    { value: '1', label: 'Association of Persons' },
    { value: '2', label: 'Sole Proprietorship' },
    { value: '3', label: 'Partnership' },
    { value: '4', label: 'Private Limited' },
    { value: '5', label: 'Public Limited' },
    { value: '6', label: 'LLP' },
    { value: '7', label: 'Trust' },
    { value: '8', label: 'Society' },
    { value: '9', label: 'Government' },
    { value: '10', label: 'Hindu Undivided Family' },
    { value: '11', label: 'Local Authority' },
    { value: '12', label: 'Non Individual' },
    { value: '13', label: 'Education' },
    { value: '14', label: 'Individual' },
    { value: '15', label: 'NGO' },
    { value: '16', label: 'One Person Company' },
    { value: '17', label: 'Artificial Juridical Person' },
  ];

  const getLabelForValue = value => {
    const option = businessEntityOptions.find(opt => opt.value === value);
    return option ? option.label : 'Unknown Entity';
  };
  const businessEntityLabel = getLabelForValue(businessEntity);
  return (
    <form>
      <div className={css.sectionContainer}>
        <FieldTextInput
          type="text"
          id="panCardName"
          name="panCardName"
          value={panCardName}
          initialValue={panCardName}
          className={css.custom}
          label="Name as in PAN card"
          validate={validators.required('Name is required')}
        />
        <FieldTextInput
          type="text"
          id="panCardNumber"
          name="panCardNumber"
          initialValue={panCardNumber}
          className={css.custom}
          label="PAN card Number"
          validate={validators.validpanNumber('Invalid PAN card number')}
        />

        <FieldSelect
          id="businessEntity"
          name="businessEntity"
          className={css.custom}
          label="Business entity"
        >
          <option value="" disabled>
            {businessEntityLabel || 'Select a Business Entity'}
          </option>
          {businessEntityOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </FieldSelect>

        {/* <FieldTextInput
        type="text"
        id="GSTNumber"    
        name="GSTNumber"
        initialValue={GSTNumber}   
        className={css.custom}
        label= "GST Number"
        validate={validators.validGSTNumber('Invalid GST Number')}
      /> */}
        <FieldPhoneNumberInput
          type="number"
          id="phoneNumber"
          name="phoneNumber"
          initialValue={phoneNumber}
          className={css.custom}
          label="Phone Number"
          validate={validators.validPhoneNumber('Invalid Phone Number')}
        />
        <FieldTextInput
          type="number"
          id="Quantity"
          name="Quantity"
          initialValue={Quantity}
          className={css.custom}
          label="Monthly Expected Volume"
        />
        <FieldTextInput
          type="text"
          id="BusinessName"
          name="BusinessName"
          initialValue={BusinessName}
          className={css.custom}
          label="Business Name"
          placeholder="Enter your business name"
        />
        <FieldTextInput
          type="text"
          id="BankAccountNumber"
          name="BankAccountNumber"
          initialValue={BankAccountNumber}
          className={css.custom}
          label="Bank Account Number"
        />
        <FieldTextInput
          type="text"
          id="AccountHolderName"
          name="AccountHolderName"
          initialValue={AccountHolderName}
          className={css.custom}
          label="Bank Account Holder Name"
        />
        <FieldTextInput
          type="text"
          id="IfscCode"
          name="IfscCode"
          initialValue={IfscCode}
          className={css.custom}
          label="IFSC Code"
          placeholder="Enter the Bank IFSC Code"
          validate={validators.validIfscCode('Invalid IFSC Code')}
        />
      </div>
    </form>
  );
};

const ErrorsMaybe = props => {
  const { stripeAccountError, stripeAccountLinkError } = props;

  const errorMessage = isStripeError(stripeAccountError) ? (
    <FormattedMessage
      id="StripeConnectAccountForm.createStripeAccountFailedWithStripeError"
      values={{ stripeMessage: stripeAccountError.apiErrors[0].meta.stripeMessage }}
    />
  ) : stripeAccountError ? (
    <FormattedMessage id="StripeConnectAccountForm.createStripeAccountFailed" />
  ) : isStripeError(stripeAccountLinkError) ? (
    <FormattedMessage
      id="StripeConnectAccountForm.createStripeAccountLinkFailedWithStripeError"
      values={{ stripeMessage: stripeAccountLinkError.apiErrors[0].meta.stripeMessage }}
    />
  ) : stripeAccountLinkError ? (
    <FormattedMessage id="StripeConnectAccountForm.createStripeAccountLinkFailed" />
  ) : null;

  return errorMessage ? <div className={css.error}>{errorMessage}</div> : null;
};

const StripeConnectAccountFormComponent = props => {
  const [showCardUpdateInput, setShowCardUpdateInput] = useState(false);
  const config = useConfiguration();
  const { onSubmit, ...restOfProps } = props;
  const isUpdate = props.stripeConnected;
  const stripePublishableKey = config.stripe.publishableKey;
  const supportedCountries = config.stripe.supportedCountries;

  return (
    <FinalForm
      {...restOfProps}
      onSubmit={values => onSubmit({ ...values })}
      mutators={{
        ...arrayMutators,
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          children,
          stripeAccountError,
          stripeAccountLinkError,
          disabled,
          handleSubmit,
          inProgress,
          intl,
          invalid,
          pristine,
          ready,
          savedCountry,
          stripeAccountFetched,
          stripeBankAccountLastDigits,
          submitButtonText,
          form,
          values,
          stripeConnected,
          currentUser,
          childMerchantError
        } = fieldRenderProps;

        const accountDataLoaded = stripeConnected && stripeAccountFetched && savedCountry;
        const submitInProgress = inProgress;
        const submitDisabled = pristine || invalid || disabled || submitInProgress;

        const handleFormSubmit = event => {
          handleSubmit(event);
        };

        const countryLabel = intl.formatMessage({ id: 'StripeConnectAccountForm.countryLabel' });
        const classes = classNames(rootClassName || css.root, className, {
          [css.disabled]: disabled,
        });

        const showAsRequired = pristine;

        const currentUserId = currentUser ? currentUser.id : null;

        // If the user doesn't have Stripe connected account,
        // show fields for country and bank account.
        // Otherwise, show only possibility the edit bank account
        // because Stripe doesn't allow user to change the country
        const stripeAccountFields = !stripeConnected ? (
          <CreateStripeAccountFields
            stripeConnected={stripeConnected}
            disabled={disabled}
            showAsRequired={showAsRequired}
            countryLabel={countryLabel}
            supportedCountries={supportedCountries}
            marketplaceRootURL={config.marketplaceRootURL}
            defaultMCC={config.stripe.defaultMCC}
            currentUserId={currentUserId}
            form={form}
            values={values}
            intl={intl}
            currentUser={currentUser}
          />
        ) : (
          <UpdateStripeAccountFields
            disabled={disabled}
            countryLabel={countryLabel}
            supportedCountries={supportedCountries}
            savedCountry={savedCountry}
            stripeBankAccountLastDigits={stripeBankAccountLastDigits}
            showCardUpdateInput={showCardUpdateInput}
            values={values}
            submitInProgress={submitInProgress}
            setShowCardUpdateInput={setShowCardUpdateInput}
            intl={intl}
          />
        );

        const stripeConnectedAccountTermsLink = (
          <ExternalLink href="https://stripe.com/connect-account/legal" className={css.termsLink}>
            <FormattedMessage id="StripeConnectAccountForm.stripeConnectedAccountTermsLink" />
          </ExternalLink>
        );

        // Don't show the submit button while fetching the Stripe account data
        const submitButtonMaybe = (
          <>
            {/* <p className={css.termsText}>
                <FormattedMessage
                  id="StripeConnectAccountForm.stripeToSText"
                  values={{ stripeConnectedAccountTermsLink }}
                />
              </p> */}

              {childMerchantError !== '' ? <span className={css.errorMsg}>{childMerchantError}</span> : null}
            <Button
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={ready}
            >
              {submitButtonText || (
                <FormattedMessage id="StripeConnectAccountForm.submitButtonText" />
              )}
            </Button>
          </>
        );

        return (
          <Form className={classes} onSubmit={handleFormSubmit}>
            {!stripeConnected || accountDataLoaded ? (
              stripeAccountFields
            ) : (
              <div className={css.savedInformation}>
                <FormattedMessage id="StripeConnectAccountForm.loadingStripeAccountData" />
              </div>
            )}

            <ErrorsMaybe
              stripeAccountError={stripeAccountError}
              stripeAccountLinkError={stripeAccountLinkError}
            />

            {children}

            {submitButtonMaybe}
          </Form>
        );
      }}
    />
  );
};

StripeConnectAccountFormComponent.defaultProps = {
  className: null,
  currentUser: null,
  stripeAccountError: null,
  disabled: false,
  inProgress: false,
  ready: false,
  savedCountry: null,
  stripeBankAccountLastDigits: null,
  submitButtonText: null,
  fieldRenderProps: null,
};

StripeConnectAccountFormComponent.propTypes = {
  currentUser: propTypes.currentUser,
  className: string,
  stripeAccountError: object,
  disabled: bool,
  inProgress: bool,
  ready: bool,
  savedCountry: string,
  stripeBankAccountLastDigits: string,
  stripeAccountFetched: bool.isRequired,
  submitButtonText: string,
  fieldRenderProps: shape({
    handleSubmit: func,
    invalid: bool,
    pristine: bool,
    values: object,
  }),

  // from injectIntl
  intl: intlShape.isRequired,
};

const StripeConnectAccountForm = compose(injectIntl)(StripeConnectAccountFormComponent);

export default StripeConnectAccountForm;
